import { useLocation, useHistory } from "react-router-dom";
import PageContainer from "../../../components/PageContainer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MdOutlineArrowBack } from "react-icons/md";
import FormatDataService from "../../../services/util/FormatData";
import Loading from "../../../components/Loading";
import { Alert, Button, Dropdown, Table, Spinner, Tabs, Tab } from "react-bootstrap";
import RelatorioApiService from "../../../services/api/RelatorioApiService";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FiInfo } from "react-icons/fi";

const popupStyle = {
    position: "absolute",
    top: "-40%",
    left: "-600%",
    transform: "translateX(-82%)",
    backgroundColor: "#e9e9e9",
    color: "#212529",
    border: "1px solid #dee2e6",
    borderRadius: "5px",
    padding: "5px 10px",
    zIndex: 1000,
    whiteSpace: "nowrap",
    boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
    width: "auto",
    fontWeight: "normal",
};

function RelatorioHistoricoHorasExtrasView() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [historicoHorasExtrasData, setHistoricoHorasExtrasData] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: "NomeCompleto", direction: "asc" });
    const [isExporting, setIsExporting] = useState(false);
    const [visiblePeriodoTooltip, setVisiblePeriodoTooltip] = useState(null);
    const [activeTab, setActiveTab] = useState("pago-colaborador");
    const [selectedGestor, setSelectedGestor] = useState(null);

    const location = useLocation();
    const history = useHistory();
    const { data } = location.state || { data: { periodoInicial: "", periodoFinal: "" } };
    const { periodoInicial, periodoFinal } = data;
    
    const formatHours = useCallback(
        (value) => FormatDataService.hoursToMinutesInBR(value),
        []
    );

    const formatPeriodoFull = useCallback((periodo) => FormatDataService.formatarPeriodoPorExtenso(periodo), []);

    /* FETCH */
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const dataInicial = FormatDataService.formatarDataParaYYYYMM(periodoInicial);
            const dataFinal = FormatDataService.formatarDataParaYYYYMM(periodoFinal);

            const response = await RelatorioApiService.getHistoricoHorasExtras(
                dataInicial,
                dataFinal
            );

            setHistoricoHorasExtrasData(response?.data?.data ?? {});

        } catch (err) {
            console.error("Erro ao carregar relatório:", err);
            setError("Erro ao carregar dados do relatório. Tente novamente.");
        } finally {
            setLoading(false);
        }
    }, [periodoInicial, periodoFinal]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    /* MODELAGEM: um registro por colaborador, com totais por período (50%, 100%, total) */
    const { colaboradoresPorPeriodo, periodosOrdenados } = useMemo(() => {
        if (!historicoHorasExtrasData || Array.isArray(historicoHorasExtrasData)) {
            return { colaboradoresPorPeriodo: {}, periodosOrdenados: [] };
        }

        const periodos = Object.keys(historicoHorasExtrasData)
            .filter((key) => /^\d{4}-\d{2}$/.test(key))
            .sort(
            (a, b) => Number(a.replace("-", "")) - Number(b.replace("-", ""))
        );

        const byColaborador = {};

        for (const periodo of periodos) {
            const periodData = historicoHorasExtrasData[periodo] ?? {};
            const arr50 = periodData?.["50"] ?? [];
            const arr100 = periodData?.["100"] ?? [];
            const arrAdicionalNoturno = periodData?.["adicionalNoturno"] ?? [];

            const accumulate = (arr, key) => {
                for (const item of arr) {
                    const name = item?.NomeCompleto ?? "Não informado";

                    if (!byColaborador[name]) {
                        byColaborador[name] = {
                            NomeCompleto: name,
                            Gestor: item?.Gestor ?? "Não informado",
                            
                            periodos: {},
                        };
                    }

                    if (!byColaborador[name].periodos[periodo]) {
                        byColaborador[name].periodos[periodo] = {
                            percentual50: 0,
                            percentual100: 0,
                            adicionalNoturno: 0,
                            foPagTotal: 0,
                            custoProjetoTotal: 0,
                        };
                    }

                    const horas = Number(item?.horasExtras) || 0;
                    const foPagTotal = Number(item?.foPagTotal) || 0;
                    const custoProjetoTotal = Number(item?.custoProjetoTotal) || 0;
                    byColaborador[name].periodos[periodo][key] += horas;
                    byColaborador[name].periodos[periodo].foPagTotal += foPagTotal;
                    byColaborador[name].periodos[periodo].custoProjetoTotal += custoProjetoTotal;
                }
            };

            accumulate(arr50, "percentual50");
            accumulate(arr100, "percentual100");
            accumulate(arrAdicionalNoturno, "adicionalNoturno");

            for (const name of Object.keys(byColaborador)) {
                if (!byColaborador[name].periodos[periodo]) {
                    byColaborador[name].periodos[periodo] = {
                        percentual50: 0,
                        percentual100: 0,
                        adicionalNoturno: 0,
                        foPagTotal: 0,
                        custoProjetoTotal: 0,
                    };
                }
            }
        }

        return { colaboradoresPorPeriodo: byColaborador, periodosOrdenados: periodos };
    }, [historicoHorasExtrasData]);

    /* Lista de colaboradores ordenada (para tabela única) */
    const sortedColaboradores = useMemo(() => {
        const list = Object.values(colaboradoresPorPeriodo);
        const key = sortConfig.key;
        const dir = sortConfig.direction === "asc" ? 1 : -1;

        const compare = (a, b) => {
            const aVal = key === "NomeCompleto" ? a.NomeCompleto : a.Gestor;
            const bVal = key === "NomeCompleto" ? b.NomeCompleto : b.Gestor;
            const order = (aVal ?? "")
                .toString()
                .toLowerCase()
                .localeCompare((bVal ?? "").toString().toLowerCase());
            return order * dir;
        };

        return [...list].sort(compare);
    }, [colaboradoresPorPeriodo, sortConfig]);

    /* Lista de gestores únicos */
    const gestores = useMemo(() => {
        const set = new Set(
            Object.values(colaboradoresPorPeriodo)
                .map((c) => c.Gestor)
                .filter(Boolean)
        );
        return [...set].sort((a, b) => a.localeCompare(b));
    }, [colaboradoresPorPeriodo]);

    /* Colaboradores filtrados por gestor */
    const filteredColaboradores = useMemo(() => {
        if (!selectedGestor) return sortedColaboradores;
        return sortedColaboradores.filter((c) => c.Gestor === selectedGestor);
    }, [sortedColaboradores, selectedGestor]);

    /* Totais por mês baseados nos colaboradores filtrados */
    const totaisFiltradosPorMes = useMemo(() => {
        const result = {};
        for (const periodo of periodosOrdenados) {
            let total50 = 0, total100 = 0, totalAdicionalNoturno = 0;
            let totalFoPag = 0, totalCustoProjeto = 0;
            for (const colab of filteredColaboradores) {
                const p = colab.periodos[periodo] ?? {};
                total50 += p.percentual50 || 0;
                total100 += p.percentual100 || 0;
                totalAdicionalNoturno += p.adicionalNoturno || 0;
                totalFoPag += p.foPagTotal || 0;
                totalCustoProjeto += p.custoProjetoTotal || 0;
            }
            result[periodo] = {
                total50,
                total100,
                totalAdicionalNoturno,
                totalMes: total50 + total100 + totalAdicionalNoturno,
                totalFoPag,
                totalCustoProjeto,
            };
        }
        return result;
    }, [filteredColaboradores, periodosOrdenados]);

    /* Totais gerais baseados nos colaboradores filtrados */
    const totaisGeraisFiltrados = useMemo(() => {
        return periodosOrdenados.reduce(
            (acc, periodo) => {
                const t = totaisFiltradosPorMes[periodo] ?? {};
                acc.totalHoras += t.totalMes || 0;
                acc.totalFoPagTotal += t.totalFoPag || 0;
                acc.totalCustoProjetoTotal += t.totalCustoProjeto || 0;
                acc.porGrupo["50"].totalHoras += t.total50 || 0;
                acc.porGrupo["100"].totalHoras += t.total100 || 0;
                acc.porGrupo.adicionalNoturno.totalHoras += t.totalAdicionalNoturno || 0;
                return acc;
            },
            {
                totalHoras: 0,
                totalFoPagTotal: 0,
                totalCustoProjetoTotal: 0,
                porGrupo: {
                    "50": { totalHoras: 0 },
                    "100": { totalHoras: 0 },
                    adicionalNoturno: { totalHoras: 0 },
                },
            }
        );
    }, [totaisFiltradosPorMes, periodosOrdenados]);

    /* Totais globais por mês vindos da API */
    const totaisPorMes = useMemo(() => {
        if (!historicoHorasExtrasData || Array.isArray(historicoHorasExtrasData)) {
            return {};
        }

        const result = {};

        for (const periodo of periodosOrdenados) {
            const periodData = historicoHorasExtrasData[periodo] ?? {};
            const arr50 = periodData?.["50"] ?? [];
            const arr100 = periodData?.["100"] ?? [];
            const arrAdicionalNoturno = periodData?.["adicionalNoturno"] ?? [];
            const itensDoPeriodo = [...arr50, ...arr100, ...arrAdicionalNoturno];

            const totalFoPagCalculado = itensDoPeriodo.reduce(
                (acc, item) => acc + (Number(item?.foPagTotal) || 0),
                0
            );
            const totalCustoProjetoCalculado = itensDoPeriodo.reduce(
                (acc, item) => acc + (Number(item?.custoProjetoTotal) || 0),
                0
            );

            result[periodo] = {
                total50: Number(periodData.total_50) || 0,
                total100: Number(periodData.total_100) || 0,
                totalAdicionalNoturno: Number(periodData.total_adicionalNoturno) || 0,
                totalMes: Number(periodData.totalMes) || 0,
                totalFoPag:
                    Number(periodData.totalFoPagTotal) ||
                    Number(periodData.totalFoPag) ||
                    totalFoPagCalculado,
                totalCustoProjeto:
                    Number(periodData.totalCustoProjetoTotal) ||
                    Number(periodData.totalCustoProjeto) ||
                    totalCustoProjetoCalculado,
            };
        }

        return result;
    }, [historicoHorasExtrasData, periodosOrdenados]);

    const totaisGerais = useMemo(() => {
        const totalGeralApi = historicoHorasExtrasData?.totalGeral;
        if (totalGeralApi) {
            return {
                totalHoras: Number(totalGeralApi.totalHoras) || 0,
                totalFoPagTotal: Number(totalGeralApi.totalFoPagTotal) || 0,
                totalCustoProjetoTotal: Number(totalGeralApi.totalCustoProjetoTotal) || 0,
                porGrupo: {
                    "50": {
                        totalHoras: Number(totalGeralApi?.porGrupo?.["50"]?.totalHoras) || 0,
                        totalFoPagTotal: Number(totalGeralApi?.porGrupo?.["50"]?.totalFoPagTotal) || 0,
                        totalCustoProjetoTotal: Number(totalGeralApi?.porGrupo?.["50"]?.totalCustoProjetoTotal) || 0,
                    },
                    "100": {
                        totalHoras: Number(totalGeralApi?.porGrupo?.["100"]?.totalHoras) || 0,
                        totalFoPagTotal: Number(totalGeralApi?.porGrupo?.["100"]?.totalFoPagTotal) || 0,
                        totalCustoProjetoTotal: Number(totalGeralApi?.porGrupo?.["100"]?.totalCustoProjetoTotal) || 0,
                    },
                    adicionalNoturno: {
                        totalHoras: Number(totalGeralApi?.porGrupo?.adicionalNoturno?.totalHoras) || 0,
                        totalFoPagTotal: Number(totalGeralApi?.porGrupo?.adicionalNoturno?.totalFoPagTotal) || 0,
                        totalCustoProjetoTotal: Number(totalGeralApi?.porGrupo?.adicionalNoturno?.totalCustoProjetoTotal) || 0,
                    },
                },
            };
        }

        return periodosOrdenados.reduce(
            (acc, periodo) => {
                const totais = totaisPorMes[periodo] ?? {};
                acc.totalHoras += Number(totais.totalMes) || 0;
                acc.totalFoPagTotal += Number(totais.totalFoPag) || 0;
                acc.totalCustoProjetoTotal += Number(totais.totalCustoProjeto) || 0;
                acc.porGrupo["50"].totalHoras += Number(totais.total50) || 0;
                acc.porGrupo["100"].totalHoras += Number(totais.total100) || 0;
                acc.porGrupo.adicionalNoturno.totalHoras += Number(totais.totalAdicionalNoturno) || 0;
                return acc;
            },
            {
                totalHoras: 0,
                totalFoPagTotal: 0,
                totalCustoProjetoTotal: 0,
                porGrupo: {
                    "50": { totalHoras: 0, totalFoPagTotal: 0, totalCustoProjetoTotal: 0 },
                    "100": { totalHoras: 0, totalFoPagTotal: 0, totalCustoProjetoTotal: 0 },
                    adicionalNoturno: { totalHoras: 0, totalFoPagTotal: 0, totalCustoProjetoTotal: 0 },
                },
            }
        );
    }, [historicoHorasExtrasData, periodosOrdenados, totaisPorMes]);

    /* Períodos a destacar em amarelo (sempre 2 meses em aberto conforme 5º dia útil) */
    const periodosParaDestacar = useMemo(() => {
        const hoje = new Date();
        const ano = hoje.getFullYear();
        const mes = hoje.getMonth();
        const formatPeriodo = (date) =>
            `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

        const dataMesAtual = new Date(ano, mes, 1);
        const dataMesAnterior = new Date(ano, mes - 1, 1);
        const dataMesAnteriorAoAnterior = new Date(ano, mes - 2, 1);

        const mesAtual = formatPeriodo(dataMesAtual);
        const mesAnterior = formatPeriodo(dataMesAnterior);
        const mesAnteriorAoAnterior = formatPeriodo(dataMesAnteriorAoAnterior);

        const getQuintoDiaUtil = (y, m) => {
            let count = 0;
            for (let day = 1; day <= 31; day++) {
                const d = new Date(y, m, day);
                if (d.getMonth() !== m) break;
                const dow = d.getDay();
                if (dow !== 0 && dow !== 6) {
                    count++;
                    if (count === 5) return d;
                }
            }
            return null;
        };

        const quintoDiaUtil = getQuintoDiaUtil(ano, mes);
        const hojeStart = new Date(ano, mes, hoje.getDate());
        const passouQuintoDiaUtil =
            quintoDiaUtil &&
            hojeStart >= new Date(quintoDiaUtil.getFullYear(), quintoDiaUtil.getMonth(), quintoDiaUtil.getDate());

        const periodosNoFiltro = new Set(periodosOrdenados);

        // Regra:
        // - Antes do 5º dia útil do mês corrente:
        //   mantém abertos os dois meses anteriores (ex: até 04/03 -> jan e fev em amarelo)
        // - A partir do 5º dia útil do mês corrente:
        //   fecha o mês mais antigo e mantém abertos mês anterior e mês atual
        //   (ex: a partir de 05/03 -> fev e mar em amarelo)
        const desejados = passouQuintoDiaUtil
            ? [mesAnterior, mesAtual]
            : [mesAnteriorAoAnterior, mesAnterior, mesAtual];

        // Destaca apenas os períodos que existem no filtro
        return desejados.filter((p) => periodosNoFiltro.has(p));
    }, [periodosOrdenados]);

    const setPeriodosDestacar = useMemo(
        () => new Set(periodosParaDestacar),
        [periodosParaDestacar]
    );

    /* SORT */
    const handleSort = useCallback((key) => {
        setSortConfig((prev) => ({
            key,
            direction:
                prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    }, []);

    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) {
            return <span style={{ opacity: 0.3 }}>▴</span>;
        }
        return <span>{sortConfig.direction === "asc" ? "▴" : "▾"}</span>;
    };

    /* EXPORT */
    const exportDataInExcel = useCallback(async () => {
        setIsExporting(true);

        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Histórico Horas Extras");

            const yellowFill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFFF4CC" },
            };

            // Cabeçalho com seis subcolunas por período:
            // 50%, 100%, Adicional Noturno, Total, Folha de Pagamento, Custo do Projeto
            const headerCols = [
                "Colaborador",
                "Gestor",
                // ["Mês Ano 50%", "Mês Ano 100%", "Mês Ano Adic. Noturno", "Mês Ano Total",
                //  "Mês Ano Folha de Pagamento", "Mês Ano Custo do Projeto"] para cada período
                ...periodosOrdenados.flatMap((p) => {
                    const label = formatPeriodoFull(p);
                    return [
                        `${label} 50%`,
                        `${label} 100%`,
                        `${label} Adic. Noturno`,
                        `${label} Total`,
                        `${label} Folha de Pagamento`,
                        `${label} Custo do Projeto`,
                    ];
                }),
            ];

            const headerRow = worksheet.addRow(headerCols);

            headerRow.eachCell((cell, colNumber) => {
                cell.font = { bold: true };
                // colNumber starts at 1, not 0
                // A partir da coluna 3 (índice 2), de seis em seis colunas
                if (colNumber >= 3) {
                    // Calcula em qual período estamos para destacar bloco correto
                    const periodoIdx = Math.floor((colNumber - 3) / 6);
                    if (
                        periodosOrdenados[periodoIdx] &&
                        setPeriodosDestacar.has(periodosOrdenados[periodoIdx])
                    ) {
                        cell.fill = yellowFill;
                    }
                }
            });

            // Função para formatar horas no padrão "HH:mm"
            const formatExcelTime = (value) => {
                // value em horas (decimal), ex: 2.25 => 02:15
                const totalMinutes = Math.round(Number(value) * 60);
                const h = Math.floor(totalMinutes / 60);
                const m = totalMinutes % 60;
                // padStart(2, "0") nas duas casas
                return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
            };

            filteredColaboradores.forEach((row) => {
                // Para cada colaborador, adiciona 6 colunas por período:
                // 50%, 100%, Adic. Noturno, Total, Folha de Pagamento, Custo do Projeto
                const periodoCols = periodosOrdenados.flatMap((periodo) => {
                    const cell = row.periodos[periodo] ?? {
                        percentual50: 0,
                        percentual100: 0,
                        adicionalNoturno: 0,
                        foPagTotal: 0,
                        custoProjetoTotal: 0,
                    };
                    const v50 = cell.percentual50 || 0;
                    const v100 = cell.percentual100 || 0;
                    const vAdicionalNoturno = cell.adicionalNoturno || 0;
                    const total = v50 + v100 + vAdicionalNoturno;
                    const foPagTotal = cell.foPagTotal || 0;
                    const custoProjetoTotal = cell.custoProjetoTotal || 0;
                    return [
                        v50 ? formatExcelTime(v50) : "—",
                        v100 ? formatExcelTime(v100) : "—",
                        vAdicionalNoturno ? formatExcelTime(vAdicionalNoturno) : "—",
                        total ? formatExcelTime(total) : "—",
                        foPagTotal
                            ? foPagTotal.toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })
                            : "—",
                        custoProjetoTotal
                            ? custoProjetoTotal.toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })
                            : "—",
                    ];
                });

                const rowData = [
                    row.NomeCompleto,
                    row.Gestor,
                    ...periodoCols,
                ];
                const excelRow = worksheet.addRow(rowData);

                excelRow.eachCell((cell, colNumber) => {
                    // Colunas de período começam na 3 e vão de 6 em 6
                    if (colNumber >= 3) {
                        const periodoIdx = Math.floor((colNumber - 3) / 6);
                        if (
                            periodosOrdenados[periodoIdx] &&
                            setPeriodosDestacar.has(periodosOrdenados[periodoIdx])
                        ) {
                            cell.fill = yellowFill;
                        }
                    }
                });
            });

            const gestorNoNomeArquivo = selectedGestor ? ` - ${selectedGestor}` : "";
            const inicialFormatadoExport = formatPeriodoFull(periodoInicial);
            const finalFormatadoExport = formatPeriodoFull(periodoFinal);
            const nomeArquivo =
                inicialFormatadoExport !== "Data inválida" && finalFormatadoExport !== "Data inválida"
                    ? `Relatório Histórico de Horas Extras${gestorNoNomeArquivo} - ${inicialFormatadoExport} a ${finalFormatadoExport}`
                    : `Relatório Histórico de Horas Extras${gestorNoNomeArquivo}`;
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
            });
            saveAs(blob, `${nomeArquivo}.xlsx`);
        } catch (err) {
            console.error("Erro ao exportar:", err);
            setError("Erro ao exportar dados. Tente novamente.");
        } finally {
            setIsExporting(false);
        }
    }, [filteredColaboradores, periodosOrdenados, periodoInicial, periodoFinal, setPeriodosDestacar, selectedGestor, formatPeriodoFull]);

    /* Render */
    const hasData = sortedColaboradores.length > 0;

    useEffect(() => {
        if (loading || error || !hasData) {
            return undefined;
        }
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, [loading, error, hasData]);

    const actions = [
        {
            label: "Voltar",
            icon: <MdOutlineArrowBack />,
            color: "info",
            type: "button",
            onClick: () => history.goBack(),
        },
    ];

    const inicialFormatado = formatPeriodoFull(periodoInicial);
    const finalFormatado = formatPeriodoFull(periodoFinal);
    const title = 
        inicialFormatado !== "Data inválida" && finalFormatado !== "Data inválida"
            ? `Relatório Histórico de Horas Extras - ${inicialFormatado} a ${finalFormatado}`
            : "Relatório Histórico de Horas Extras";

    return (
        <PageContainer title={title} actions={actions}>
            {loading && <Loading message="Carregando..." />}

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {!loading && !error && !hasData && (
                <Alert variant="primary">
                    Nenhum dado disponível para o período selecionado.
                </Alert>
            )}

            {!loading && !error && hasData && (
                <div className="historico-horas-extras-scroll-root">
                    <div className="d-flex mb-3 historico-horas-extras-toolbar" style={{ gap: "0.5rem" }}>
                        <Button
                            onClick={exportDataInExcel}
                            disabled={isExporting}
                        >
                            {isExporting ? (
                                <>
                                    <Spinner size="sm" animation="border" className="mr-2" />
                                    Exportando...
                                </>
                            ) : (
                                "Exportar"
                            )}
                        </Button>
                        <Dropdown>
                            <Dropdown.Toggle variant="light" id="dd-gestor">
                                Filtrar por Gestor:{" "}
                                <span style={{ fontWeight: "bold" }}>
                                    {selectedGestor ?? "Todos"}
                                </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu style={{ maxHeight: 300, overflowY: "auto" }}>
                                <Dropdown.Item onClick={() => setSelectedGestor(null)}>
                                    Todos
                                </Dropdown.Item>
                                {gestores.map((g) => (
                                    <Dropdown.Item key={g} onClick={() => setSelectedGestor(g)}>
                                        {g}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                    <style>{`
                        .historico-horas-extras-scroll-root {
                            display: flex;
                            flex-direction: column;
                            height: calc(100vh - var(--header-height) - 11.25rem);
                            height: calc(100dvh - var(--header-height) - 1.7rem);
                            min-height: 240px;
                            overflow: hidden;
                        }
                        .historico-horas-extras-scroll-root > .historico-horas-extras-toolbar,
                        .historico-horas-extras-scroll-root > .historico-horas-extras-totais {
                            flex-shrink: 0;
                        }
                        .historico-horas-extras-tabs-wrap {
                            flex: 1;
                            min-height: 0;
                            display: flex;
                            flex-direction: column;
                            overflow: hidden;
                        }
                        .historico-horas-extras-tabs-wrap > .nav-tabs {
                            flex-shrink: 0;
                        }
                        .historico-horas-extras-tabs-wrap > .tab-content {
                            flex: 1;
                            min-height: 0;
                            overflow: hidden;
                            display: flex;
                            flex-direction: column;
                        }
                        .historico-horas-extras-tabs-wrap .tab-pane.active {
                            flex: 1;
                            min-height: 0;
                            overflow: hidden;
                            display: flex !important;
                            flex-direction: column;
                        }
                        .historico-horas-extras-scroll-root .table-responsive-custom {
                            flex: 1;
                            min-height: 0;
                            overflow-x: auto;
                            overflow-y: auto;
                            max-width: calc(100vw - 265px);
                        }
                        .colaborador-sticky {
                            position: sticky !important;
                            left: -0.25px;
                            z-index: 3;
                            border: 1px solid #dee2e6;
                        }
                        .colaborador-sticky::after {
                            content: '';
                            position: absolute;
                            top: 0;
                            right: -3px;
                            width: 6px;
                            height: 100%;
                            background: linear-gradient(to right, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0));
                            z-index: 9;
                            pointer-events: none;
                        }
                        th.colaborador-sticky {
                            z-index: 4;
                            background-color: #f5f5f5 !important;
                        }
                        .table-striped > tbody > tr:nth-of-type(odd) > td.colaborador-sticky {
                            background-color: #e9e9e9 !important;
                        }
                        .table-striped > tbody > tr:nth-of-type(even) > td.colaborador-sticky {
                            background-color: #f5f5f5 !important;
                        }
                        .table-hover > tbody > tr:nth-of-type(even):hover > td.colaborador-sticky {
                            background-color:rgb(233, 233, 233) !important;
                        }
                        .table-hover > tbody > tr:nth-of-type(odd):hover > td.colaborador-sticky {
                            background-color: rgb(228, 228, 228) !important;
                        }
                        @media (max-width: 1200px) {
                            .historico-horas-extras-scroll-root .table-responsive-custom {
                                max-width: calc(100vw - 230px);
                            }
                        }
                        @media (max-width: 768px) {
                            .historico-horas-extras-scroll-root .table-responsive-custom {
                                max-width: 100vw;
                            }
                            .historico-horas-extras-scroll-root {
                                height: calc(100vh - var(--header-height) - 10rem);
                                height: calc(100dvh - var(--header-height) - 10rem);
                            }
                        }
                        td:hover .hours-extras-tooltip {
                            opacity: 1 !important;
                            visibility: visible !important;
                        }
                        th.periodo-destacado,
                        td.periodo-destacado {
                            background-color: rgb(255, 244, 204) !important;
                        }
                        .table-historico-horas-extras {
                            border-collapse: separate;
                            border-spacing: 0;
                        }
                        .table-historico-horas-extras thead th {
                            position: sticky;
                            top: 0;
                            z-index: 5;
                            background-color: #f5f5f5;
                            box-shadow: inset 0 -1px 0 #dee2e6;
                        }
                        .table-historico-horas-extras thead th.colaborador-sticky {
                            z-index: 7;
                            background-color: #f5f5f5 !important;
                            box-shadow: inset 0 -1px 0 #dee2e6, inset -1px 0 0 #dee2e6;
                        }
                        .table-historico-horas-extras thead th.periodo-destacado {
                            background-color: rgb(255, 244, 204) !important;
                        }
                    `}</style>
                    
                        <div className="w-100 mb-1 historico-horas-extras-totais">
                            <Table bordered style={{background: "#e1eaf3"}}>
                                <thead>
                                    <tr>
                                        <th>Totais</th>
                                        <th>Total 50%</th>
                                        <th>Total 100%</th>
                                        <th>Total Adicional Noturno</th>
                                        <th>Folha de Pagamento</th>
                                        <th>Custo dos Projetos</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><b>{formatHours(totaisGeraisFiltrados.totalHoras)}</b></td>
                                        <td><b>{formatHours(totaisGeraisFiltrados.porGrupo["50"].totalHoras)}</b></td>
                                        <td><b>{formatHours(totaisGeraisFiltrados.porGrupo["100"].totalHoras)}</b></td>
                                        <td><b>{formatHours(totaisGeraisFiltrados.porGrupo.adicionalNoturno.totalHoras)}</b></td>
                                        <td><b>
                                            {`R$ ${totaisGeraisFiltrados.totalFoPagTotal.toLocaleString("pt-BR", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}`}
                                        </b></td>
                                        <td><b>
                                            {`R$ ${totaisGeraisFiltrados.totalCustoProjetoTotal.toLocaleString("pt-BR", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}`}
                                        </b></td>
                                    </tr>
                                    
                                </tbody>
                            </Table>
                        </div>
                    <div className="historico-horas-extras-tabs-wrap">
                    <Tabs
                        id="historico-horas-extras-tabs"
                        activeKey={activeTab}
                        onSelect={(key) => setActiveTab(key || "pago-colaborador")}
                        className="mb-2"
                    >
                        <Tab eventKey="pago-colaborador" title="Pago ao colaborador">
                            <div className="table-responsive-custom">
                                <Table striped bordered hover className="table-historico-horas-extras">
                                    <thead>
                                        <tr>
                                            {[
                                                { key: "NomeCompleto", label: "Colaborador" },
                                                { key: "Gestor", label: "Gestor" },
                                            ].map((col, idx) => {
                                                const baseStyle =
                                                    idx === 0
                                                        ? { width: "160px", minWidth: "160px" }
                                                        : { width: "160px", minWidth: "160px" };

                                                return (
                                                    <th
                                                        key={col.key}
                                                        role="button"
                                                        onClick={() => handleSort(col.key)}
                                                        className={col.key === "NomeCompleto" ? "colaborador-sticky" : ""}
                                                        style={{ ...baseStyle, whiteSpace: "nowrap" }}
                                                    >
                                                        {col.label} <SortIcon column={col.key} />
                                                    </th>
                                                );
                                            })}
                                            {periodosOrdenados.map((periodo) => {
                                                const labelPeriodo = formatPeriodoFull(periodo);
                                                const isDestacado = setPeriodosDestacar.has(periodo);

                                                const headerContent = isDestacado ? (
                                                    <>
                                                        {labelPeriodo}
                                                        <span
                                                            onMouseEnter={() => setVisiblePeriodoTooltip(periodo)}
                                                            onMouseLeave={() => setVisiblePeriodoTooltip(null)}
                                                            className="ml-1 info-icon"
                                                            style={{ position: "relative", cursor: "pointer" }}
                                                        >
                                                            <FiInfo />
                                                            {visiblePeriodoTooltip === periodo && (
                                                                <div className="hover-popup" style={popupStyle}>
                                                                    Horas Extras em processamento
                                                                </div>
                                                            )}
                                                        </span>
                                                    </>
                                                ) : (
                                                    labelPeriodo
                                                );
                                                return (
                                                    <th
                                                        key={periodo}
                                                        className={`col-periodo ${isDestacado ? "periodo-destacado" : ""}`}
                                                        style={{
                                                            minWidth: 160,
                                                            width: 160,
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {headerContent}
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredColaboradores.map((row, rowIndex) => {
                                            const tooltipOpensUp =
                                                filteredColaboradores.length >= 2 &&
                                                rowIndex >= filteredColaboradores.length - 2;
                                            return (
                                                <tr key={row.NomeCompleto}>
                                                    <td
                                                        className="colaborador-sticky"
                                                        style={{
                                                            width: "160px",
                                                            minWidth: "160px",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {row.NomeCompleto}
                                                    </td>
                                                    <td
                                                        style={{
                                                            width: "160px",
                                                            minWidth: "160px",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {row.Gestor}
                                                    </td>
                                                    {periodosOrdenados.map((periodo) => {
                                                        const cell = row.periodos[periodo] ?? {
                                                            percentual50: 0,
                                                            percentual100: 0,
                                                            adicionalNoturno: 0,
                                                            foPagTotal: 0,
                                                            custoProjetoTotal: 0,
                                                        };
                                                        const total =
                                                            (cell.percentual50 || 0) +
                                                            (cell.percentual100 || 0) +
                                                            (cell.adicionalNoturno || 0);
                                                        const totalFoPag = cell.foPagTotal || 0;
                                                        const totalCustoProjeto = cell.custoProjetoTotal || 0;
                                                        const hasTooltip = total > 0 || totalFoPag > 0 || totalCustoProjeto > 0;
                                                        const totalStr = formatHours(total);
                                                        const str50 = formatHours(cell.percentual50 || 0);
                                                        const str100 = formatHours(cell.percentual100 || 0);
                                                        const strAdicionalNoturno = formatHours(cell.adicionalNoturno || 0);
                                                        const valorExibido = activeTab === "pago-projeto" ? totalCustoProjeto : totalFoPag;
                                                        return (
                                                            <td
                                                                key={periodo}
                                                                className={`col-periodo position-relative ${setPeriodosDestacar.has(periodo) ? "periodo-destacado" : ""}`}
                                                                style={{
                                                                    overflow: "visible",
                                                                    whiteSpace: "nowrap",
                                                                    minWidth: 160,
                                                                    width: 160,
                                                                    cursor: hasTooltip ? "pointer" : undefined,
                                                                }}
                                                            >
                                                                {valorExibido
                                                                    ? `R$ ${valorExibido.toLocaleString("pt-BR", {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    })}`
                                                                    : "—"}
                                                                {hasTooltip && (
                                                                    <div
                                                                        className="hours-extras-tooltip"
                                                                        style={{
                                                                            position: "absolute",
                                                                            left: "50%",
                                                                            ...(tooltipOpensUp
                                                                                ? {
                                                                                      bottom: "70%",
                                                                                      transform:
                                                                                          "translateX(-50%) translateY(-6px)",
                                                                                  }
                                                                                : {
                                                                                      top: "70%",
                                                                                      transform:
                                                                                          "translateX(-50%) translateY(6px)",
                                                                                  }),
                                                                            background: "#e9ecef",
                                                                            border: "1px solid #000",
                                                                            borderRadius: "4px",
                                                                            padding: "8px 10px",
                                                                            fontSize: "13px",
                                                                            color: "#000",
                                                                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                                                            opacity: 0,
                                                                            visibility: "hidden",
                                                                            transition: "opacity 0.15s, visibility 0.15s",
                                                                            zIndex: 1050,
                                                                            pointerEvents: "none",
                                                                            whiteSpace: "nowrap",
                                                                        }}
                                                                    >
                                                                        <div>{totalStr} Totais</div>
                                                                        <div>{str50} 50%</div>
                                                                        <div>{str100} 100%</div>
                                                                        <div>{strAdicionalNoturno} Adic. Noturno</div>
                                                                        <div>R$ {totalFoPag.toLocaleString("pt-BR", {
                                                                                minimumFractionDigits: 2,
                                                                                maximumFractionDigits: 2,
                                                                            })} Folha de Pagamento
                                                                        </div>
                                                                        <div> R$ {totalCustoProjeto.toLocaleString("pt-BR", {
                                                                                minimumFractionDigits: 2,
                                                                                maximumFractionDigits: 2,
                                                                            })} Custo Projeto
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                        <tr>
                                            <td className="colaborador-sticky"><b>Total:</b></td>
                                            <td></td>
                                            {periodosOrdenados.map((periodo) => {
                                                const totais = totaisFiltradosPorMes[periodo] ?? {
                                                    total50: 0,
                                                    total100: 0,
                                                    totalAdicionalNoturno: 0,
                                                    totalMes: 0,
                                                    totalFoPag: 0,
                                                    totalCustoProjeto: 0,
                                                };

                                                const total = totais.totalMes;
                                                const totalFoPag = totais.totalFoPag || 0;
                                                const totalCustoProjeto = totais.totalCustoProjeto || 0;
                                                const hasTooltip = total > 0 || totalFoPag > 0 || totalCustoProjeto > 0;
                                                const totalStr = formatHours(total);
                                                const str50 = formatHours(totais.total50 || 0);
                                                const str100 = formatHours(totais.total100 || 0);
                                                const strAdicionalNoturno = formatHours(totais.totalAdicionalNoturno || 0);
                                                const valorExibido = activeTab === "pago-projeto" ? totalCustoProjeto : totalFoPag;

                                                return (
                                                    <td
                                                        key={periodo}
                                                        className={`col-periodo position-relative ${setPeriodosDestacar.has(periodo) ? "periodo-destacado" : ""}`}
                                                        style={{
                                                            overflow: "visible",
                                                            whiteSpace: "nowrap",
                                                            minWidth: 160,
                                                            width: 160,
                                                            cursor: hasTooltip ? "pointer" : undefined,
                                                        }}
                                                    >
                                                        <b>
                                                            {valorExibido
                                                                ? `R$ ${valorExibido.toLocaleString("pt-BR", {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2,
                                                                })}`
                                                                : "—"}
                                                        </b>
                                                        {hasTooltip && (
                                                            <div
                                                                className="hours-extras-tooltip"
                                                                style={{
                                                                    position: "absolute",
                                                                    left: "50%",
                                                                    bottom: "70%",
                                                                    transform: "translateX(-50%) translateY(-6px)",
                                                                    background: "#e9ecef",
                                                                    border: "1px solid #000",
                                                                    borderRadius: "4px",
                                                                    padding: "8px 10px",
                                                                    fontSize: "13px",
                                                                    color: "#000",
                                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                                                    opacity: 0,
                                                                    visibility: "hidden",
                                                                    transition: "opacity 0.15s, visibility 0.15s",
                                                                    zIndex: 1050,
                                                                    pointerEvents: "none",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                <div>{totalStr} Totais</div>
                                                                <div>{str50} 50%</div>
                                                                <div>{str100} 100%</div>
                                                                <div>{strAdicionalNoturno} Adic. Noturno</div>
                                                                <div>R$ {totalFoPag.toLocaleString("pt-BR", {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    })} Folha de Pagamento
                                                                </div>
                                                                <div>R$ {totalCustoProjeto.toLocaleString("pt-BR", {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    })} Custo do Projeto
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </Tab>
                        <Tab eventKey="pago-projeto" title="Pago por projeto">
                            <div className="table-responsive-custom">
                                <Table striped bordered hover className="table-historico-horas-extras">
                                    <thead>
                                        <tr>
                                            {[
                                                { key: "NomeCompleto", label: "Colaborador" },
                                                { key: "Gestor", label: "Gestor" },
                                            ].map((col, idx) => {
                                                const baseStyle =
                                                    idx === 0
                                                        ? { width: "160px", minWidth: "160px" }
                                                        : { width: "160px", minWidth: "160px" };

                                                return (
                                                    <th
                                                        key={col.key}
                                                        role="button"
                                                        onClick={() => handleSort(col.key)}
                                                        className={col.key === "NomeCompleto" ? "colaborador-sticky" : ""}
                                                        style={{ ...baseStyle, whiteSpace: "nowrap" }}
                                                    >
                                                        {col.label} <SortIcon column={col.key} />
                                                    </th>
                                                );
                                            })}
                                            {periodosOrdenados.map((periodo) => {
                                                const labelPeriodo = formatPeriodoFull(periodo);
                                                const isDestacado = setPeriodosDestacar.has(periodo);

                                                const headerContent = isDestacado ? (
                                                    <>
                                                        {labelPeriodo}
                                                        <span
                                                            onMouseEnter={() => setVisiblePeriodoTooltip(periodo)}
                                                            onMouseLeave={() => setVisiblePeriodoTooltip(null)}
                                                            className="ml-1 info-icon"
                                                            style={{ position: "relative", cursor: "pointer" }}
                                                        >
                                                            <FiInfo />
                                                            {visiblePeriodoTooltip === periodo && (
                                                                <div className="hover-popup" style={popupStyle}>
                                                                    Horas Extras em processamento
                                                                </div>
                                                            )}
                                                        </span>
                                                    </>
                                                ) : (
                                                    labelPeriodo
                                                );
                                                return (
                                                    <th
                                                        key={periodo}
                                                        className={`col-periodo ${isDestacado ? "periodo-destacado" : ""}`}
                                                        style={{
                                                            minWidth: 160,
                                                            width: 160,
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {headerContent}
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredColaboradores.map((row, rowIndex) => {
                                            const tooltipOpensUp =
                                                filteredColaboradores.length >= 2 &&
                                                rowIndex >= filteredColaboradores.length - 2;
                                            return (
                                                <tr key={row.NomeCompleto}>
                                                    <td
                                                        className="colaborador-sticky"
                                                        style={{
                                                            width: "160px",
                                                            minWidth: "160px",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {row.NomeCompleto}
                                                    </td>
                                                    <td
                                                        style={{
                                                            width: "160px",
                                                            minWidth: "160px",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {row.Gestor}
                                                    </td>
                                                    {periodosOrdenados.map((periodo) => {
                                                        const cell = row.periodos[periodo] ?? {
                                                            percentual50: 0,
                                                            percentual100: 0,
                                                            adicionalNoturno: 0,
                                                            foPagTotal: 0,
                                                            custoProjetoTotal: 0,
                                                        };
                                                        const total =
                                                            (cell.percentual50 || 0) +
                                                            (cell.percentual100 || 0) +
                                                            (cell.adicionalNoturno || 0);
                                                        const totalFoPag = cell.foPagTotal || 0;
                                                        const totalCustoProjeto = cell.custoProjetoTotal || 0;
                                                        const hasTooltip = total > 0 || totalFoPag > 0 || totalCustoProjeto > 0;
                                                        const totalStr = formatHours(total);
                                                        const str50 = formatHours(cell.percentual50 || 0);
                                                        const str100 = formatHours(cell.percentual100 || 0);
                                                        const strAdicionalNoturno = formatHours(cell.adicionalNoturno || 0);
                                                        const valorExibido = activeTab === "pago-projeto" ? totalCustoProjeto : totalFoPag;
                                                        return (
                                                            <td
                                                                key={periodo}
                                                                className={`col-periodo position-relative ${setPeriodosDestacar.has(periodo) ? "periodo-destacado" : ""}`}
                                                                style={{
                                                                    overflow: "visible",
                                                                    whiteSpace: "nowrap",
                                                                    minWidth: 160,
                                                                    width: 160,
                                                                    cursor: hasTooltip ? "pointer" : undefined,
                                                                }}
                                                            >
                                                                {valorExibido
                                                                    ? `R$ ${valorExibido.toLocaleString("pt-BR", {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    })}`
                                                                    : "—"}
                                                                {hasTooltip && (
                                                                    <div
                                                                        className="hours-extras-tooltip"
                                                                        style={{
                                                                            position: "absolute",
                                                                            left: "50%",
                                                                            ...(tooltipOpensUp
                                                                                ? {
                                                                                      bottom: "70%",
                                                                                      transform:
                                                                                          "translateX(-50%) translateY(-6px)",
                                                                                  }
                                                                                : {
                                                                                      top: "70%",
                                                                                      transform:
                                                                                          "translateX(-50%) translateY(6px)",
                                                                                  }),
                                                                            background: "#e9ecef",
                                                                            border: "1px solid #000",
                                                                            borderRadius: "4px",
                                                                            padding: "8px 10px",
                                                                            fontSize: "13px",
                                                                            color: "#000",
                                                                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                                                            opacity: 0,
                                                                            visibility: "hidden",
                                                                            transition: "opacity 0.15s, visibility 0.15s",
                                                                            zIndex: 1050,
                                                                            pointerEvents: "none",
                                                                            whiteSpace: "nowrap",
                                                                        }}
                                                                    >
                                                                        <div>{totalStr} Totais</div>
                                                                        <div>{str50} 50%</div>
                                                                        <div>{str100} 100%</div>
                                                                        <div>{strAdicionalNoturno} Adic. Noturno</div>
                                                                        <div>R$ {totalFoPag.toLocaleString("pt-BR", {
                                                                                minimumFractionDigits: 2,
                                                                                maximumFractionDigits: 2,
                                                                            })} Folha de Pagamento
                                                                        </div>
                                                                        <div> R$ {totalCustoProjeto.toLocaleString("pt-BR", {
                                                                                minimumFractionDigits: 2,
                                                                                maximumFractionDigits: 2,
                                                                            })} Custo Projeto
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                        <tr>
                                            <td className="colaborador-sticky"><b>Total:</b></td>
                                            <td></td>
                                            {periodosOrdenados.map((periodo) => {
                                                const totais = totaisFiltradosPorMes[periodo] ?? {
                                                    total50: 0,
                                                    total100: 0,
                                                    totalAdicionalNoturno: 0,
                                                    totalMes: 0,
                                                    totalFoPag: 0,
                                                    totalCustoProjeto: 0,
                                                };

                                                const total = totais.totalMes;
                                                const totalFoPag = totais.totalFoPag || 0;
                                                const totalCustoProjeto = totais.totalCustoProjeto || 0;
                                                const hasTooltip = total > 0 || totalFoPag > 0 || totalCustoProjeto > 0;
                                                const totalStr = formatHours(total);
                                                const str50 = formatHours(totais.total50 || 0);
                                                const str100 = formatHours(totais.total100 || 0);
                                                const strAdicionalNoturno = formatHours(totais.totalAdicionalNoturno || 0);
                                                const valorExibido = activeTab === "pago-projeto" ? totalCustoProjeto : totalFoPag;

                                                return (
                                                    <td
                                                        key={periodo}
                                                        className={`col-periodo position-relative ${setPeriodosDestacar.has(periodo) ? "periodo-destacado" : ""}`}
                                                        style={{
                                                            overflow: "visible",
                                                            whiteSpace: "nowrap",
                                                            minWidth: 160,
                                                            width: 160,
                                                            cursor: hasTooltip ? "pointer" : undefined,
                                                        }}
                                                    >
                                                        <b>
                                                            {valorExibido
                                                                ? `R$ ${valorExibido.toLocaleString("pt-BR", {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2,
                                                                })}`
                                                                : "—"}
                                                        </b>
                                                        {hasTooltip && (
                                                            <div
                                                                className="hours-extras-tooltip"
                                                                style={{
                                                                    position: "absolute",
                                                                    left: "50%",
                                                                    bottom: "70%",
                                                                    transform: "translateX(-50%) translateY(-6px)",
                                                                    background: "#e9ecef",
                                                                    border: "1px solid #000",
                                                                    borderRadius: "4px",
                                                                    padding: "8px 10px",
                                                                    fontSize: "13px",
                                                                    color: "#000",
                                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                                                    opacity: 0,
                                                                    visibility: "hidden",
                                                                    transition: "opacity 0.15s, visibility 0.15s",
                                                                    zIndex: 1050,
                                                                    pointerEvents: "none",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                <div>{totalStr} Totais</div>
                                                                <div>{str50} 50%</div>
                                                                <div>{str100} 100%</div>
                                                                <div>{strAdicionalNoturno} Adic. Noturno</div>
                                                                <div>R$ {totalFoPag.toLocaleString("pt-BR", {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    })} Folha de Pagamento
                                                                </div>
                                                                <div>R$ {totalCustoProjeto.toLocaleString("pt-BR", {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    })} Custo do Projeto
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </Tab>
                    </Tabs>
                    </div>
                </div>
            )}
        </PageContainer>
    );
}

export default RelatorioHistoricoHorasExtrasView;